import React, { useEffect, useState, useCallback } from 'react';
import config from "../config";
import Helper from "../helpers";
import { format, parseISO } from 'date-fns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { 
  CalendarIcon, 
  ClockIcon, 
  UsersIcon, 
  CurrencyDollarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const baseUrl = config.apiBaseUrl;

const SessionAdminViewing = () => {
    const [avaialbeSessions, setAvaialbeSessions] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [groupedSessions, setGroupedSessions] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [from, setFromDate] = useState("");
    const [to, setToDate] = useState("");
    const [fromDatePicker, setFromDatePicker] = useState(dayjs());
    const [toDatePicker, setToDatePicker] = useState(dayjs().add(9, 'day'));

    const fetchSessionsData = useCallback(async () => {
        try {
            if (!fromDatePicker || !toDatePicker) {
                console.error('Date pickers not initialized');
                return;
            }
            const from = fromDatePicker.toISOString();
            setFromDate(from);
            const to = toDatePicker.toISOString();
            setToDate(to);
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/sessions/available/${currentVenueKey}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&includeusers=true`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setSessions(data);
            setAvaialbeSessions(data.length > 0);
            // Group sessions by date and add AllBooked section
            const grouped = data.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)).reduce((acc, session) => {
                const date = session.startDate.split('T')[0];
                if (!acc[date]) {
                    acc[date] = [];
                }
                acc[date].push(session);

                if (session.users && session.users.length > 0) {
                    if (!acc['AllBooked']) {
                        acc['AllBooked'] = [];
                    }
                    let start = new Date(session.startDate);
                    const sessionItem = {
                            sessionId: session.id,
                            title: session.name,
                            startDate: session.startDate,
                            start,
                            end: new Date(start.getTime() + session.numberOfMinuets * 60000),
                            //sessionBookText: userSessionIds.includes(session.id) ? 'Booked' : 'Book',
                            availableSeats: session.availableSeats,
                            seatsTaken: session.seatsTaken,
                            numberOfMinuets: session.numberOfMinuets,
                            sessionCost: session.tokenCost,
                            users : session.users
                        };
                    
                    acc['AllBooked'].push(sessionItem);
                }
                return acc;
            }, {});
            setGroupedSessions(grouped);
            setSelectedDate('AllBooked');
            setLoading(false);
        } catch (error) {
            console.error('Error fetching sessions', error);
            setLoading(false);
        }
    }, [currentVenueKey, fromDatePicker, toDatePicker]);

    useEffect(() => {
        const initialize = async () => {
            await Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
            if (currentVenueKey) {
                fetchSessionsData();
            }
        }
        initialize();
    }, [currentVenueKey, fetchSessionsData]);

    const handleRefreshSessions = async () => {
        setLoading(true);
        await fetchSessionsData();
    };

    // const handleChange = (event, newValue) => {
    //     setSelectedDate(newValue);
    // };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    const sortedDates = ['AllBooked', ...Object.keys(groupedSessions).filter(date => date !== 'AllBooked')];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header Card */}
            <div className="card p-6 mb-6">
                <h1 className="text-3xl font-bold text-white mb-4">
                    Admin Session Management
                </h1>
                
                {/* Date Range Picker Controls */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="mb-6 p-4 bg-gray-800 rounded-lg">
                        <h3 className="text-lg font-semibold text-white mb-4">Select Date Range</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    From Date
                                </label>
                                <DatePicker
                                    value={fromDatePicker}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => setFromDatePicker(newValue)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined"
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    To Date
                                </label>
                                <DatePicker
                                    value={toDatePicker}
                                    format="DD/MM/YYYY"
                                    onChange={(newValue) => setToDatePicker(newValue)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            variant: "outlined"
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={handleRefreshSessions}
                                    className="btn-primary w-full flex items-center justify-center"
                                    disabled={loading}
                                >
                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                    {loading ? 'Loading...' : 'Refresh Sessions'}
                                </button>
                            </div>
                        </div>
                    </div>
                </LocalizationProvider>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center text-gray-300">
                        <CalendarIcon className="h-5 w-5 mr-2" />
                        <span>From: {from && format(parseISO(from), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                        <CalendarIcon className="h-5 w-5 mr-2" />
                        <span>To: {to && format(parseISO(to), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                        <UsersIcon className="h-5 w-5 mr-2" />
                        <span>Venues: {venueKeyOptions.length}</span>
                    </div>
                </div>
                <div className="mt-3 text-lg font-semibold text-blue-400">
                    Total Sessions: {sessions.length}
                </div>
            </div>

            {/* Tabs */}
            <div className="card mb-6">
                <div className="flex flex-wrap border-b border-gray-700">
                    {avaialbeSessions && sortedDates.length > 0 && sortedDates.map(date => (
                        <button
                            key={date}
                            onClick={() => setSelectedDate(date)}
                            className={`px-4 py-3 text-sm font-medium transition-colors ${
                                selectedDate === date
                                    ? 'text-blue-400 border-b-2 border-blue-400'
                                    : 'text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            <div className="text-center">
                                <div>{date === 'AllBooked' ? 'All Booked' : format(parseISO(date), 'MMM dd')}</div>
                                {date !== 'AllBooked' && (
                                    <div className="text-xs">{format(parseISO(date), 'eeee')}</div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Sessions Table */}
            {!avaialbeSessions || (groupedSessions[selectedDate] && groupedSessions[selectedDate].length === 0) ? (
                <div className="card p-8 text-center">
                    <h3 className="text-xl font-medium text-gray-400">No sessions available</h3>
                </div>
            ) : (
                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="table-header">
                                <tr>
                                    <th className="px-6 py-3 text-left">Session Details</th>
                                    <th className="px-6 py-3 text-left">Time</th>
                                    <th className="px-6 py-3 text-left">Capacity</th>
                                    <th className="px-6 py-3 text-left">Cost</th>
                                    <th className="px-6 py-3 text-left">Attendees</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {groupedSessions[selectedDate].map((session) => {
                                    const startTime = new Date(session.startDate);
                                    const endTime = new Date(startTime.getTime() + session.numberOfMinuets * 60000);
                                    
                                    return (
                                        <tr key={session.id || session.sessionId} className="hover:bg-gray-700 transition-colors">
                                            <td className="table-cell">
                                                <div>
                                                    <div className="font-medium text-white">{session.name || session.title}</div>
                                                    <div className="text-sm text-gray-400">{format(startTime, 'MMMM dd, yyyy')}</div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div className="flex items-center">
                                                    <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    <div>
                                                        <div className="font-medium">{format(startTime, 'HH:mm')}</div>
                                                        <div className="text-sm text-gray-400">
                                                            to {format(endTime, 'HH:mm')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div className="flex items-center">
                                                    <UsersIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    <div>
                                                        <div className="font-medium">
                                                            {session.seatsTaken || 0} / {session.availableSeats || 0}
                                                        </div>
                                                        <div className="text-sm text-gray-400">booked</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                <div className="flex items-center">
                                                    <CurrencyDollarIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    <div className="font-medium">
                                                        {session.sessionCost || session.tokenCost || 0} tokens
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="table-cell">
                                                {session.users && session.users.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {session.users.slice(0, 3).map((user, idx) => (
                                                            <div key={idx} className="text-sm bg-gray-600 px-2 py-1 rounded">
                                                                {user.name}
                                                            </div>
                                                        ))}
                                                        {session.users.length > 3 && (
                                                            <div className="text-xs text-gray-400">
                                                                +{session.users.length - 3} more
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500 text-sm">No attendees</span>
                                                )}
                                            </td>
                                            <td className="table-cell">
                                                <div className="flex space-x-2">
                                                    <button className="btn-outline flex items-center px-3 py-1 text-sm">
                                                        <EyeIcon className="h-4 w-4 mr-1" />
                                                        View
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionAdminViewing;
